import React from "react";
import { Ship, Anchor, Cog, Radar, Waves, Cpu } from "lucide-react";

const ASSETS = [
  {
    icon: <Ship size={22} />,
    category: "Vessels",
    items: [
      { name: "DP2 Multi-Role Support Vessels", spec: "Dynamic Positioning · 80m+ LOA · 250-tonne bollard pull" },
      { name: "Anchor Handling Tug Supply (AHTS)", spec: "Heavy anchor deployment · 12,000 BHP class" },
      { name: "Fast Crew Boats", spec: "Personnel transfer · 35-knot cruise · SOLAS-certified" },
      { name: "Barges & Deck Cargo Carriers", spec: "Heavy lift-out · up to 15,000 DWT" },
    ],
  },
  {
    icon: <Waves size={22} />,
    category: "Subsea Systems",
    items: [
      { name: "Work-class ROVs", spec: "Depth-rated 3,000m · 5-function manipulators" },
      { name: "Autonomous Underwater Vehicles (AUV)", spec: "Bathymetric & pipeline route surveys" },
      { name: "Diamond-wire Cutting Spreads", spec: "Structural cutting up to 3m diameter members" },
      { name: "Diver Support Systems", spec: "Saturation & air diving spreads · 300m depth" },
    ],
  },
  {
    icon: <Cog size={22} />,
    category: "Heavy Machinery",
    items: [
      { name: "Rig Skid & Hydraulic Torque Kits", spec: "Precision-pressure torqueing to 100,000 ft-lb" },
      { name: "Coiled Tubing Units", spec: "3-1/2\" tubing · 15,000 psi rating" },
      { name: "Cementing & Pumping Spreads", spec: "High-pressure well-integrity cementing" },
      { name: "Wireline / Slickline Trucks", spec: "Full downhole intervention capability" },
    ],
  },
  {
    icon: <Cpu size={22} />,
    category: "Digital & Simulation",
    items: [
      { name: "VR Drilling Simulator Suite", spec: "Blowout-prevention training · certified curriculum" },
      { name: "Digital Twin Compute Cluster", spec: "Real-time 3D rig replication for remote monitoring" },
      { name: "Metocean Forecasting Stack", spec: "Localized wave/wind modelling for op-planning" },
      { name: "Onshore Subsea Control Room", spec: "24/7 remote operations & telemetry monitoring" },
    ],
  },
  {
    icon: <Radar size={22} />,
    category: "Instrumentation",
    items: [
      { name: "Multibeam & Side-scan Sonar", spec: "High-resolution seabed mapping" },
      { name: "Magnetometer & Gradiometer Arrays", spec: "Buried pipeline & hazard detection" },
      { name: "Passive Acoustic Monitoring", spec: "Marine mammal protection during seismic ops" },
      { name: "Gas / Radiation / Noise Detection", spec: "Full QHSE monitoring instrumentation suite" },
    ],
  },
  {
    icon: <Anchor size={22} />,
    category: "Shore Bases",
    items: [
      { name: "Houston, Texas — Group HQ", spec: "Gulf of Mexico operations & tender office" },
      { name: "Aberdeen, Scotland", spec: "North Sea operations & talent hub" },
      { name: "Lagos, Nigeria", spec: "Gulf of Guinea operations & West Africa gateway" },
      { name: "Singapore", spec: "East Asia & rig-move logistics hub" },
    ],
  },
];

const HERO_IMG = "https://images.pexels.com/photos/8883568/pexels-photo-8883568.jpeg";

export default function Fleet() {
  return (
    <div className="text-white">
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url(${HERO_IMG})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#060A14]" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-28 pb-20">
          <div className="eyebrow mb-6">Fleet & Assets</div>
          <h1 className="font-display font-black text-5xl md:text-7xl leading-[0.95] tracking-tighter max-w-4xl">
            Steel on the water.<br />
            <span className="text-[#FF5722]">Compute on the deck.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-white/80 leading-relaxed">
            An overview of the physical and digital capacity the Group can mobilize under a single
            tender.
          </p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {ASSETS.map((a) => (
            <div key={a.category} className="border border-white/10 bg-[#0C1322] p-7 hover:border-[#FF5722] transition-colors">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-11 h-11 bg-[#131D33] text-[#FF5722] flex items-center justify-center">
                  {a.icon}
                </div>
                <div className="font-display text-xl font-bold">{a.category}</div>
              </div>
              <ul className="divide-y divide-white/5">
                {a.items.map((it) => (
                  <li key={it.name} className="py-3">
                    <div className="text-white font-medium text-[14px]">{it.name}</div>
                    <div className="text-xs text-[#94A3B8] mt-0.5 font-mono">{it.spec}</div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
